import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import * as Fonts from "../../components/Fonts";
import { LoadingModal } from "../../components/LoadingModal";
import { PlusOnlyModal } from "../../components/PlusOnlyModal";
import { PlusTag } from "../../components/PlusTag";
import { Crumb, ScreenHeader } from "../../components/SubHeader";
import { HelpChatPage, useHasPlusKeepo } from "./Chat";
import { HelpEmailPage } from "./Email";
import { Container, OptionCard, Options, Section } from "./styles";

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 6L2 7" />
  </svg>
);

const ChatIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/** How to reach the support: by e-mail, or by chat for PLUS users (the others see the PLUS offer). */
const HelpOptionsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Screens that link here (e.g. the subscription) pass where to go back to
  const back = (useLocation().state as { back?: Crumb } | null)?.back;
  const hasPlus = useHasPlusKeepo();
  const [showPlusOnly, setShowPlusOnly] = useState(false);

  if (hasPlus === undefined) {
    return <LoadingModal />;
  }

  return (
    <Container>
      <ScreenHeader
        title={t("pages.help.header.title")}
        back={back}
        mobileBack={{ label: t("pages.account.header.title"), to: "/account" }}
      />
      <Section>
        <Fonts.Label>{t("pages.help.sections.contact")}</Fonts.Label>
        <Options>
          <OptionCard type="button" onClick={() => navigate("/help/email")}>
            <span className="icon">
              <MailIcon />
            </span>
            <h4>{t("pages.help.options.email.title")}</h4>
            <p>{t("pages.help.options.email.description")}</p>
          </OptionCard>

          <OptionCard type="button" $plus onClick={() => (hasPlus ? navigate("/help/chat") : setShowPlusOnly(true))}>
            <PlusTag className="tag">✦ Keepo Plus</PlusTag>
            <span className="icon">
              <ChatIcon />
            </span>
            <h4>{t("pages.help.options.chat.title")}</h4>
            <p>{t("pages.help.options.chat.description")}</p>
          </OptionCard>
        </Options>
      </Section>

      {showPlusOnly && (
        <PlusOnlyModal feature={t("pages.help.options.chat.feature")} onClose={() => setShowPlusOnly(false)} />
      )}
    </Container>
  );
};

export const HelpPage = () => (
  <Routes>
    <Route index element={<HelpOptionsPage />} />
    <Route path="email" element={<HelpEmailPage />} />
    <Route path="chat" element={<HelpChatPage />} />
    <Route path="*" element={<Navigate to="/help" />} />
  </Routes>
);
